/**
 * Penetration model. Deliberately small, and stated on the page:
 *
 *  - A round's penetration at range comes from its SDK curve (linear between keys, held flat past
 *    the ends), or its flat value when it has no curve.
 *  - A plate's effective thickness is its thickness over the cosine of the angle between the shot
 *    and the plate's normal (the line-of-sight rule). Grazing angles are capped at MAX_ANGLE so a
 *    near-parallel face reads as very thick rather than infinite.
 *  - A plate that does not pass damage to the hull (side skirts, the gun) is spaced armour: the
 *    round spends its effective thickness there and carries on with what is left.
 *  - A plate the SDK marks as not penetrable stops the round.
 *
 * Ricochet, fuse behaviour and damage after penetration are not modelled.
 */
export interface Round { id: string; name: string; pen_mm: number | null; curve: [number, number][] | null; explosive?: boolean; vehicles?: string[]; weapons?: string[] }
export interface Plate {
  id: string; label: string; role: string; thickness_mm: number | null; thicknessFrom?: string;
  allowPen: boolean; damageParent: boolean; special?: boolean; damageAbsorbed?: number | null; damageMultiplier?: number | null;
  parts: string[]; area_m2: number;
}

export const MAX_ANGLE_DEG = 85;
const MIN_COS = Math.cos((MAX_ANGLE_DEG * Math.PI) / 180);

/** Penetration in mm at a distance in metres. */
export function penAt(r: Round, distance_m: number): number {
  const c = r.curve;
  if (!c?.length) return r.pen_mm ?? 0;
  if (distance_m <= c[0]![0]) return c[0]![1];
  for (let i = 1; i < c.length; i++) {
    const [d1, p1] = c[i]!, [d0, p0] = c[i - 1]!;
    if (distance_m <= d1) return p0 + ((p1 - p0) * (distance_m - d0)) / (d1 - d0 || 1);
  }
  return c[c.length - 1]![1];
}

/** |cos| of the angle between a shot direction and a surface normal (both unit vectors). */
export const cosIncidence = (dir: readonly number[], normal: readonly number[]) =>
  Math.max(MIN_COS, Math.abs(dir[0]! * normal[0]! + dir[1]! * normal[1]! + dir[2]! * normal[2]!));

export const effective = (thickness_mm: number, cos: number) => thickness_mm / Math.max(MIN_COS, cos);
export const angleDeg = (cos: number) => (Math.acos(Math.min(1, cos)) * 180) / Math.PI;

export type Verdict = 'pen' | 'marginal' | 'no';
/** Within this fraction of the round's penetration, call it marginal: a few degrees decide it. */
export const MARGIN = 0.1;
export function verdict(pen: number, eff: number, plate: Pick<Plate, 'allowPen'>): Verdict {
  if (!plate.allowPen) return 'no';
  if (pen >= eff * (1 + MARGIN)) return 'pen';
  if (pen >= eff * (1 - MARGIN)) return 'marginal';
  return 'no';
}

export interface Hit { plate: Plate; cos: number }
export interface ShotResult {
  verdict: Verdict;
  /** Plates in the order the round met them, with the effective thickness and what was left after. */
  layers: { plate: Plate; angle: number; eff: number; left: number }[];
  /** The plate that decided it: the first one that damages the hull, or the one that stopped the round. */
  decisive: Plate | null;
  /** Internal parts (ammo, engine, fuel) the round reaches once through. */
  behind: Plate[];
}

/**
 * Walk a ray's hits in order. Spaced armour spends penetration and passes the round on; the first
 * hull plate decides. Internal modules behind a defeated plate are reported, not scored.
 */
export function resolveShot(pen: number, hits: Hit[]): ShotResult {
  const layers: ShotResult['layers'] = [];
  let left = pen;
  let decisive: Plate | null = null, v: Verdict = 'no';
  let i = 0;
  for (; i < hits.length; i++) {
    const { plate, cos } = hits[i]!;
    if (isInternal(plate)) continue;
    const eff = plate.thickness_mm == null ? Infinity : effective(plate.thickness_mm, cos);
    const vd = verdict(left, eff, plate);
    layers.push({ plate, angle: angleDeg(cos), eff, left: Math.max(0, left - eff) });
    if (plate.damageParent || !plate.allowPen || vd === 'no') { decisive = plate; v = plate.damageParent || !plate.allowPen ? vd : 'no'; i++; break; }
    left -= eff;
  }
  const behind = v === 'no' ? [] : hits.slice(i).map((h) => h.plate).filter(isInternal);
  return { verdict: v, layers, decisive, behind: [...new Map(behind.map((p) => [p.id, p])).values()] };
}

export const isInternal = (p: Pick<Plate, 'role'>) => p.role === 'ammo' || p.role === 'engine' || p.role === 'fuel';
